'use client';

import { CircleX, Star, ThumbsDown, ThumbsUp } from 'lucide-react';

import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import RichTextEditor from '@/components/rich-text-editor/rich-text-editor';
import { RTE_EXTENSIONS } from '@/components/rich-text-editor/types/rich-text-editor';
import DatePicker from '@/components/ui/date-picker';
import { PhoneInput } from '@/components/ui/phone-input';

import AttachmentField from '@features/form-editing/components/common/fields/attachment-field';
import BorderTextField from '@features/form-editing/components/common/fields/border-text-field';
import DropdownField from '@features/form-editing/components/common/fields/dropdown-field';
import MultiSelectField from '@features/form-editing/components/common/fields/multi-select-field';
import SingleSelectField from '@features/form-editing/components/common/fields/single-select-field';
import VideoRecordingField from '@features/form-editing/components/common/fields/video-recording';

import { getFileInputAcceptTypesString } from '@/utils/common';
import { cn } from '@/utils/styles';

const RICH_TEXT_BASIC_EXTENSIONS: RTE_EXTENSIONS[] = [
	RTE_EXTENSIONS.STARTER_KIT,
	RTE_EXTENSIONS.UNDERLINE,
	RTE_EXTENSIONS.BULLET_LIST,
	RTE_EXTENSIONS.ORDERED_LIST,
	RTE_EXTENSIONS.LIST_ITEM,
	RTE_EXTENSIONS.LIST_KEYMAP,
	RTE_EXTENSIONS.PLACEHOLDER,
];

type SelectFieldRef = {
	focusOnFirstOption: () => void;
};

type FieldInputRendererRef = {
	focusFirstOption: () => void;
};

/**
 * Minimal field descriptor accepted by FieldInputRenderer.
 * ISFormInputField satisfies this type structurally, as do leaner types
 * like ISFeedbackTemplateQuestion (when adapted).
 */
type FieldInputRendererField = {
	id: string;
	type: string;
	options?: string[];
	fileTypes?: string[];
	allowOther?: boolean;
};

/**
 * Props for interactive (fill-mode) rendering.
 * When provided, the component renders editable inputs instead of
 * the static/edit-preview variants used during form construction.
 */
type FieldInteractiveProps = {
	value?: string;
	onValueChange?: (value: string) => void;
	rating?: string | null;
	onRatingChange?: (rating: string) => void;
	disabled?: boolean;
};

type FieldInputRendererProps = {
	inputField: FieldInputRendererField;
	editMode?: boolean;
	netFocusedField?: boolean;
	onChangeOptions?: (updatedOptions: string[]) => void;
	onAllowOtherChange?: (value: boolean) => void;
	/** When provided, the component renders interactive (fill-mode) inputs. */
	interactiveProps?: FieldInteractiveProps;
	className?: string;
};

const SELECT_FIELD_TYPES = new Set<string>(['DROPDOWN', 'SINGLE_SELECT', 'MULTI_SELECT']);

const FieldInputRenderer = forwardRef<FieldInputRendererRef, FieldInputRendererProps>(
	(
		{
			inputField,
			editMode,
			netFocusedField = false,
			onChangeOptions = () => {},
			onAllowOtherChange = () => {},
			interactiveProps,
			className,
		},
		ref
	) => {
		const dropdownRef = useRef<SelectFieldRef | null>(null);
		const singleRef = useRef<SelectFieldRef | null>(null);
		const multiRef = useRef<SelectFieldRef | null>(null);

		useImperativeHandle(
			ref,
			() => ({
				focusFirstOption: () => {
					if (inputField.type === 'DROPDOWN') dropdownRef.current?.focusOnFirstOption();
					else if (inputField.type === 'SINGLE_SELECT') singleRef.current?.focusOnFirstOption();
					else if (inputField.type === 'MULTI_SELECT') multiRef.current?.focusOnFirstOption();
				},
			}),
			[inputField.type]
		);

		const { type } = inputField;

		if (type === 'SHORT_TEXT' || type === 'LINK' || type === 'NUMBER') {
			return (
				<BorderTextField
					type={type === 'NUMBER' ? 'NUMBER_INPUT' : 'INPUT'}
					placeholder={interactiveProps ? 'Your answer...' : ''}
					value={interactiveProps?.value ?? ''}
					onChangeValue={interactiveProps?.onValueChange}
					fillMode={!!interactiveProps}
					disabled={interactiveProps?.disabled}
					autoUpdateValueOnChangeInitialValue={!!interactiveProps}
					customInputClassName={cn(interactiveProps ? undefined : 'w-[300px]', className)}
				/>
			);
		}

		if (type === 'LONG_TEXT') {
			return (
				<BorderTextField
					type="TEXTAREA"
					placeholder={interactiveProps ? 'Your answer...' : ''}
					value={interactiveProps?.value ?? ''}
					onChangeValue={interactiveProps?.onValueChange}
					fillMode={!!interactiveProps}
					disabled={interactiveProps?.disabled}
					autoUpdateValueOnChangeInitialValue={!!interactiveProps}
					customInputClassName={cn(className)}
				/>
			);
		}

		if (type === 'FILE_UPLOAD') {
			return (
				<AttachmentField
					fieldId={inputField.id}
					editMode={editMode}
					acceptedFileTypes={getFileInputAcceptTypesString(inputField.fileTypes ?? [])}
				/>
			);
		}

		if (type === 'VIDEO') {
			return (
				<VideoRecordingField
					fieldId={inputField.id}
					editMode={editMode}
					disabled={false}
				/>
			);
		}

		if (type === 'DROPDOWN') {
			return (
				<DropdownField
					options={inputField.options ?? []}
					editMode={editMode}
					isFocusedInput={netFocusedField}
					onChangeOptions={onChangeOptions}
					allowOther={inputField.allowOther}
					onAllowOtherChange={onAllowOtherChange}
					ref={dropdownRef}
				/>
			);
		}

		if (type === 'SINGLE_SELECT') {
			return (
				<SingleSelectField
					options={inputField.options ?? []}
					editMode={editMode}
					isFocusedInput={netFocusedField}
					onChangeOptions={onChangeOptions}
					onAllowOtherChange={onAllowOtherChange}
					allowOther={inputField.allowOther}
					ref={singleRef}
				/>
			);
		}

		if (type === 'MULTI_SELECT') {
			return (
				<MultiSelectField
					options={inputField.options ?? []}
					editMode={editMode}
					isFocusedInput={netFocusedField}
					allowOther={inputField.allowOther}
					onAllowOtherChange={onAllowOtherChange}
					onChangeOptions={onChangeOptions}
					ref={multiRef}
				/>
			);
		}

		if (type === 'RICH_TEXT') {
			return (
				<RichTextEditor
					description={interactiveProps?.value ?? ''}
					onChangeDescription={interactiveProps?.onValueChange}
					editMode={editMode}
					isFocusedField={netFocusedField}
					placeholder={'Type here...'}
					allowedExtensions={RICH_TEXT_BASIC_EXTENSIONS}
					hideToolbar={!editMode}
					hideFocusUnderline
					contentClassName={cn('min-h-[62px]', className)}
					className={cn(
						'rounded-md border border-border p-2',
						editMode ? 'max-h-[240px] overflow-y-auto' : ''
					)}
				/>
			);
		}

		if (type === 'PHONE_NUMBER') {
			// Without a country the flag button renders empty; match what the fill page defaults to.
			return (
				<PhoneInput
					className="w-full max-w-[300px]"
					defaultCountry={'IN'}
					disabled
				/>
			);
		}

		if (type === 'DATE') {
			return <DatePicker disabled={editMode} />;
		}

		if (type === 'RATING') {
			const ratingPills = [
				{
					value: 'STRONG_NO',
					label: 'Strong No',
					Icon: CircleX,
					selectedBg: 'bg-red-700',
					hoverBg: 'hover:bg-red-50',
				},
				{
					value: 'NO',
					label: 'No',
					Icon: ThumbsDown,
					selectedBg: 'bg-red-700',
					hoverBg: 'hover:bg-red-50',
				},
				{
					value: 'YES',
					label: 'Yes',
					Icon: ThumbsUp,
					selectedBg: 'bg-green-700',
					hoverBg: 'hover:bg-green-100',
				},
				{
					value: 'STRONG_YES',
					label: 'Strong Yes',
					Icon: Star,
					selectedBg: 'bg-green-800',
					hoverBg: 'hover:bg-green-50',
				},
			];
			return (
				<div className="flex flex-wrap gap-1.5 py-1">
					{ratingPills.map(({ value, label, Icon, selectedBg, hoverBg }) => {
						const isSelected = interactiveProps?.rating === value;
						return interactiveProps ? (
							<button
								key={value}
								type="button"
								onClick={() => interactiveProps.onRatingChange?.(isSelected ? '' : value)}
								disabled={interactiveProps.disabled}
								className={cn(
									'flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 transition-all',
									isSelected
										? `${selectedBg} text-white`
										: `bg-accent/80 text-secondary ${hoverBg}`,
									interactiveProps.disabled && 'cursor-not-allowed opacity-50'
								)}
							>
								<Icon className="size-3.5 shrink-0" />
								<span className="whitespace-nowrap text-xs font-medium">{label}</span>
							</button>
						) : (
							<div
								key={value}
								className="flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-accent/80 px-3"
							>
								<Icon className="size-3.5 shrink-0 text-slate-400" />
								<span className="whitespace-nowrap text-xs font-medium text-slate-400">
									{label}
								</span>
							</div>
						);
					})}
				</div>
			);
		}

		return null;
	}
);

FieldInputRenderer.displayName = 'FieldInputRenderer';

export { SELECT_FIELD_TYPES };
export type { FieldInputRendererRef, FieldInputRendererField, FieldInteractiveProps };
export default FieldInputRenderer;
